import { useQuery } from '@apollo/client';
import { unique } from 'radash';
import { useMemo } from 'react';
import { GET_COMPONENTS_CREATE_REQUESTS } from '@api/queries/components';
import { GET_USERS_FOR_SELECT } from '@api/queries/users';
import { GET_COMPONENT_VENDORS_BY_IDS } from '@api/queries/vendor';
import { type SimpleUser } from '@models/SimpleUser';
import { flatMapEdges } from '@utils/flatMapEdges';
import { type ComponentCreateRequestMapped } from '@pages/notification-components/models/ComponentCreateRequestMapped';

// Client-side ceiling per users request; release has no shared `@api/pagination`.
const MAX_PAGE_SIZE = 1000;

export interface UseNewComponentRequests {
    requests: ComponentCreateRequestMapped[];
}

export function useNewComponentRequests(): UseNewComponentRequests {
    const getUser = (userId: string): SimpleUser => {
        return (
            users.find((user) => user.id === userId) || {
                id: userId,
                firstName: 'Unknown',
                lastName: 'User',
            }
        );
    };

    const { data: requestsData } = useQuery(GET_COMPONENTS_CREATE_REQUESTS);
    const requestsNodes = flatMapEdges(
        requestsData?.componentCreateRequestsView,
    );

    const userIds = useMemo(
        () =>
            unique(
                requestsNodes.map((node) => node.requesterID).filter(Boolean),
            ),
        [requestsNodes],
    );
    const { data: usersData } = useQuery(GET_USERS_FOR_SELECT, {
        variables: {
            where: {
                idIn: userIds,
            },
            first: Math.min(userIds.length, MAX_PAGE_SIZE),
        },
        skip: userIds.length === 0,
    });

    const componentVendorIds = requestsNodes
        .map((node) => node.componentVendorID)
        .filter((id): id is string => Boolean(id));
    const { data: vendorsData } = useQuery(GET_COMPONENT_VENDORS_BY_IDS, {
        variables: {
            ids: componentVendorIds,
        },
        skip: componentVendorIds.length === 0,
    });

    const users =
        flatMapEdges(usersData?.users).map((u) => ({
            id: u.id,
            firstName: u.firstName,
            lastName: u.lastName,
        })) || [];

    const requests: ComponentCreateRequestMapped[] = useMemo(
        () =>
            requestsNodes.map((node) => ({
                ...node,
                requester: getUser(node.requesterID),
                comments: node.comments.map((comment) => ({
                    content: comment.comment,
                    createTime: comment.createTime,
                    user: getUser(comment.userID),
                })),
                componentVendor: vendorsData?.componentVendors.edges.find(
                    (edge) => edge.node.id === node.componentVendorID,
                )?.node,
            })) || [],
        [requestsNodes, users, vendorsData],
    );

    return {
        requests,
    };
}
